import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";
import api from "@/lib/api";

const PAGES = {
  terms: {
    title: "Terms of Service",
    updated: "March 2026",
    sections: [
      { heading: "1. Acceptance of Terms", body: "By accessing and using the services provided by Keyz's Epic Adventure Tours (\"the Company\"), registered and operating in Saint Lucia, West Indies, you agree to be bound by these Terms of Service. The Company reserves the right to modify these terms at any time." },
      { heading: "2. Booking & Payment", body: "All tour prices are quoted in US Dollars (USD) and are subject to availability. A booking is confirmed only upon receipt of full payment. The Company accepts payments via PayPal and authorized discount codes. Prices may fluctuate based on our dynamic pricing system, including Early Bird and Last-Minute discounts." },
      { heading: "3. Eligibility & Health Requirements", body: "Certain tours have physical requirements and age restrictions. It is the responsibility of the participant to ensure they meet the health and fitness standards required. Participants must disclose any medical conditions that may affect their ability to safely participate. The Company reserves the right to refuse participation if safety concerns arise." },
      { heading: "4. Safety Waiver & Assumption of Risk", body: "Adventure tourism activities carry inherent risks. By booking a tour, you acknowledge and accept these risks. All participants are required to sign a physical safety waiver before the commencement of any adventure activity (zip-lining, hiking, snorkeling, etc.). The Company maintains comprehensive insurance coverage and employs certified guides, but cannot be held liable for injuries resulting from participant negligence or failure to follow safety instructions." },
      { heading: "5. Weather-Related Changes", body: "Saint Lucia's tropical climate may necessitate tour modifications or cancellations due to adverse weather conditions including tropical storms, heavy rainfall, or sea conditions. In such cases, the Company will offer either a full rescheduling at no additional cost or a complete refund. The Company's decision on weather-related safety is final and made in the best interest of participant safety." },
      { heading: "6. Intellectual Property", body: "All content on this website, including photographs, descriptions, logos, and branding, is the property of Keyz's Epic Adventure Tours and is protected under the laws of Saint Lucia and international copyright conventions. Unauthorized use is prohibited." },
      { heading: "7. Governing Law", body: "These terms are governed by the laws of Saint Lucia, West Indies. Any disputes arising from these terms or the services provided shall be subject to the exclusive jurisdiction of the courts of Saint Lucia." },
    ]
  },
  privacy: {
    title: "Privacy Policy",
    updated: "March 2026",
    sections: [
      { heading: "1. Information We Collect", body: "We collect personal information that you voluntarily provide when booking tours, signing up for our newsletter, or contacting us. This includes your name, email address, phone number, and payment information. We also automatically collect certain technical data including IP addresses, browser type, and usage patterns through cookies and similar technologies." },
      { heading: "2. How We Use Your Information", body: "Your information is used to: process and confirm tour bookings, send booking confirmations and updates via email, provide customer support, send promotional offers (only with your consent), operate our referral and loyalty programs, and improve our services. We will never sell your personal data to third parties." },
      { heading: "3. Data Storage & Security", body: "Your data is stored securely using industry-standard encryption. Payment processing is handled by PayPal; we do not store credit card information on our servers. Our systems are hosted with enterprise-grade security providers and comply with international data protection standards." },
      { heading: "4. Cookies & Tracking", body: "We use essential cookies for site functionality and session management. Analytics cookies help us understand how visitors use our site. You can manage cookie preferences through your browser settings." },
      { heading: "5. Third-Party Services", body: "We use the following third-party services: PayPal for payment processing, Resend for email communications, and Google for authentication. Each service has its own privacy policy governing data handling." },
      { heading: "6. Your Rights", body: "You have the right to: access your personal data, request correction of inaccurate data, request deletion of your data, opt out of marketing communications, and lodge complaints with the relevant data protection authority. To exercise these rights, contact us at privacy@keyzadventures.com." },
      { heading: "7. Data Retention", body: "Booking records are retained for 7 years for legal and accounting purposes. Marketing data is retained until you opt out. Account data is deleted upon request, subject to legal retention requirements." },
    ]
  },
  cancellation: {
    title: "Cancellation & Refund Policy",
    updated: "March 2026",
    sections: [
      { heading: "1. Customer-Initiated Cancellations", body: "Cancellations made 48 hours or more before the scheduled tour time: Full refund. Cancellations made 24-48 hours before: 50% refund. Cancellations made less than 24 hours before or no-shows: No refund. All cancellation requests must be made via email to bookings@keyzadventures.com." },
      { heading: "2. Weather-Related Cancellations", body: "If the Company cancels a tour due to adverse weather conditions (tropical storms, dangerous sea conditions, heavy rainfall, etc.), guests will receive a full refund or the option to reschedule at no additional cost. Weather decisions are made by our operations team based on local meteorological reports and safety assessments. Saint Lucia's hurricane season runs from June to November; tours during this period carry a higher likelihood of weather-related changes." },
      { heading: "3. Company-Initiated Cancellations", body: "In rare cases where the Company must cancel due to operational reasons (equipment maintenance, guide unavailability, minimum participant requirements not met), a full refund will be issued within 5-7 business days." },
      { heading: "4. Modification of Bookings", body: "Date changes are subject to availability and must be requested at least 48 hours in advance. One free date change is permitted per booking. Subsequent changes may incur a 10% administrative fee." },
      { heading: "5. Refund Processing", body: "Refunds are processed to the original payment method within 5-7 business days of approval. Discount codes and referral credits used during booking are restored upon refund. Currency exchange rate differences between booking and refund dates are the responsibility of the customer." },
      { heading: "6. Force Majeure", body: "The Company is not liable for cancellations caused by events beyond our control, including but not limited to natural disasters, pandemics, government restrictions, civil unrest, or volcanic activity. In such cases, we will work with affected guests to reschedule or provide credit for future bookings." },
    ]
  },
  contact: {
    title: "Contact Us",
    updated: "March 2026",
    isContact: true,
    sections: []
  }
};

export default function LegalPage({ page }) {
  const content = PAGES[page];
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");

  const [contactSending, setContactSending] = useState(false);

  const handleContactSubmit = async () => {
    if (!contactName || !contactEmail || !contactMessage) {
      toast.error("Please fill in all fields");
      return;
    }
    setContactSending(true);
    try {
      await api.post("/contact", { name: contactName, email: contactEmail, message: contactMessage });
      toast.success("Message sent! Check your email for a confirmation.");
      setContactName(""); setContactEmail(""); setContactMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    } finally {
      setContactSending(false);
    }
  };

  useSEO(content ? {    title: content.title,
    description: `${content.title} for Keyz's Epic Adventure Tours, Saint Lucia. Updated ${content.updated}.`,
  } : {});

  if (!content) return null;

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      <div className="pt-28 pb-24 px-6 max-w-3xl mx-auto">
        <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-3">Legal</p>
        <h1 className="font-cormorant text-4xl sm:text-5xl font-light text-midnight mb-2" data-testid="legal-page-title">
          {content.title}
        </h1>
        <p className="font-manrope text-xs text-midnight/30 mb-10">Last updated: {content.updated}</p>

        {content.isContact ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-cormorant text-2xl font-light text-midnight mb-6">Get in Touch</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-sandy mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-manrope text-sm font-medium text-midnight">Location</p>
                    <p className="font-manrope text-sm text-midnight/50">L Abbayee, Bexon, Saint Lucia, West Indies</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-sandy mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-manrope text-sm font-medium text-midnight">Phone</p>
                    <p className="font-manrope text-sm text-midnight/50">+1 (758) 485-6161</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-sandy mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-manrope text-sm font-medium text-midnight">Email</p>
                    <p className="font-manrope text-sm text-midnight/50">info@kzepictours.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-sandy mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-manrope text-sm font-medium text-midnight">Hours</p>
                    <p className="font-manrope text-sm text-midnight/50">Mon-Sat: 7:00 AM - 6:00 PM</p>
                    <p className="font-manrope text-sm text-midnight/50">Sun: 8:00 AM - 4:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className="font-cormorant text-2xl font-light text-midnight mb-6">Send a Message</h2>
              <div className="space-y-4">
                <Input data-testid="contact-name" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Your Name" className="font-manrope text-sm" />
                <Input data-testid="contact-email" type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="Your Email" className="font-manrope text-sm" />
                <Textarea data-testid="contact-message" value={contactMessage} onChange={e => setContactMessage(e.target.value)} placeholder="How can we help?" rows={5} className="font-manrope text-sm" />
                <Button
                  data-testid="contact-submit-btn"
                  onClick={handleContactSubmit}
                  disabled={contactSending}
                  className="rounded-full bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-sm px-6"
                >
                  {contactSending ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8" data-testid="legal-content">
            {content.sections.map((s, i) => (
              <div key={i}>
                <h2 className="font-cormorant text-xl text-midnight mb-3">{s.heading}</h2>
                <p className="font-manrope text-sm text-midnight/60 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
